import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Settings,
  Person,
  Notifications,
  Palette,
  Language,
  Storage,
  Backup,
  Security,
  Email,
  Phone,
  LocationOn,
  Edit,
  Save,
  Cancel,
  Delete,
  Add,
  Visibility,
  VisibilityOff,
  CloudUpload,
  Download,
  Refresh,
  Info,
  Warning,
  CheckCircle,
  ImportExport,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import PasswordStrengthMeter from '../../components/Auth/PasswordStrengthMeter';
import { evaluatePassword } from '../../utils/passwordStrength';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import DataPortabilityPanel from '../../components/Settings/DataPortabilityPanel';
import { getAuthToken } from '../../lib/authToken';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ padding: '24px' }}>{children}</div>}
    </div>
  );
}

// Profile Dialog Component - Defined outside main component to prevent re-renders
interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  profileFormData: any;
  setProfileFormData: (data: any) => void;
  isSavingProfile: boolean;
  onSave: () => void;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({
  open,
  onClose,
  profileFormData,
  setProfileFormData,
  isSavingProfile,
  onSave,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Edit Profile</DialogTitle>
    <DialogContent>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="First Name"
              value={profileFormData.firstName}
              onChange={(e) => setProfileFormData({ ...profileFormData, firstName: e.target.value })}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Last Name"
              value={profileFormData.lastName}
              onChange={(e) => setProfileFormData({ ...profileFormData, lastName: e.target.value })}
              fullWidth
              required
            />
          </Grid>
        </Grid>
        <TextField
          label="Email"
          type="email"
          value={profileFormData.email}
          fullWidth
          required
          disabled
          helperText="Contact an admin to change your email address"
        />
        <TextField
          label="Phone"
          value={profileFormData.phone}
          onChange={(e) => setProfileFormData({ ...profileFormData, phone: e.target.value })}
          fullWidth
          helperText="Format: +1234567890 or 1234567890 (7-15 digits)"
          placeholder="+1234567890"
        />
        <TextField
          label="Role"
          value={profileFormData.role}
          onChange={(e) => setProfileFormData({ ...profileFormData, role: e.target.value })}
          fullWidth
        />
        <TextField
          label="Department"
          value={profileFormData.department}
          onChange={(e) => setProfileFormData({ ...profileFormData, department: e.target.value })}
          fullWidth
        />
        <TextField
          label="Address"
          value={profileFormData.address}
          onChange={(e) => setProfileFormData({ ...profileFormData, address: e.target.value })}
          fullWidth
          placeholder="123 Main Street"
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="City"
              value={profileFormData.city}
              onChange={(e) => setProfileFormData({ ...profileFormData, city: e.target.value })}
              fullWidth
              placeholder="New York"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="State"
              value={profileFormData.state}
              onChange={(e) => setProfileFormData({ ...profileFormData, state: e.target.value })}
              fullWidth
              placeholder="NY"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Zip Code"
              value={profileFormData.zipCode}
              onChange={(e) => setProfileFormData({ ...profileFormData, zipCode: e.target.value })}
              fullWidth
              placeholder="10001"
            />
          </Grid>
        </Grid>
        <FormControl fullWidth>
          <InputLabel>Timezone</InputLabel>
          <Select
            value={profileFormData.timezone}
            label="Timezone"
            onChange={(e) => setProfileFormData({ ...profileFormData, timezone: e.target.value })}
          >
            <MenuItem value="America/New_York">Eastern Time</MenuItem>
            <MenuItem value="America/Chicago">Central Time</MenuItem>
            <MenuItem value="America/Denver">Mountain Time</MenuItem>
            <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isSavingProfile}>
        Cancel
      </Button>
      <Button 
        onClick={onSave} 
        variant="contained"
        disabled={isSavingProfile}
      >
        {isSavingProfile ? 'Saving...' : 'Save Changes'}
      </Button>
    </DialogActions>
  </Dialog>
);

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  avatar: string;
  role: string;
  department: string;
  location: string;
  timezone: string;
  language: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  caseUpdates: boolean;
  documentUploads: boolean;
  billingReminders: boolean;
  calendarEvents: boolean;
  securityAlerts: boolean;
  marketingEmails: boolean;
}

interface AppearanceSettings {
  theme: 'light' | 'dark' | 'auto';
  primaryColor: string;
  fontSize: 'small' | 'medium' | 'large';
  compactMode: boolean;
  showAnimations: boolean;
  sidebarCollapsed: boolean;
}

interface SystemSettings {
  autoSave: boolean;
  autoBackup: boolean;
  dataRetention: number;
  maxFileSize: number;
  allowedFileTypes: string[];
  sessionTimeout: number;
  language: string;
  timezone: string;
}

// Password Dialog Component - like ProfileDialog above, this MUST stay outside
// SettingsPage. Declared inside, it became a new component type on every
// keystroke, so React unmounted the dialog and remounted it, and the field
// being typed into lost focus after each character.
interface PasswordDialogProps {
  open: boolean;
  onClose: () => void;
  passwordFormData: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  };
  setPasswordFormData: (data: any) => void;
  showPassword: boolean;
  setShowPassword: (value: boolean) => void;
  isChangingPassword: boolean;
  onSave: () => void;
}

const PasswordDialog: React.FC<PasswordDialogProps> = ({
  open,
  onClose,
  passwordFormData,
  setPasswordFormData,
  showPassword,
  setShowPassword,
  isChangingPassword,
  onSave,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Change Password</DialogTitle>
    <DialogContent>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <TextField
          label="Current Password"
          type={showPassword ? 'text' : 'password'}
          value={passwordFormData.currentPassword}
          onChange={(e) => setPasswordFormData({ ...passwordFormData, currentPassword: e.target.value })}
          fullWidth
          required
          autoComplete="current-password"
        />
        <TextField
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={passwordFormData.newPassword}
          onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
          fullWidth
          required
          autoComplete="new-password"
          helperText="Min 12 characters with upper, lower, number, and special character"
        />
        <PasswordStrengthMeter password={passwordFormData.newPassword} />
        <TextField
          label="Confirm New Password"
          type={showPassword ? 'text' : 'password'}
          value={passwordFormData.confirmPassword}
          onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
          fullWidth
          required
          autoComplete="new-password"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
          }
          label="Show Password"
        />
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isChangingPassword}>
        Cancel
      </Button>
      <Button
        onClick={onSave}
        variant="contained"
        disabled={isChangingPassword}
      >
        {isChangingPassword ? 'Changing...' : 'Change Password'}
      </Button>
    </DialogActions>
  </Dialog>
);

const SettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [isSavingAppearance, setIsSavingAppearance] = useState(false);
  const [isSavingSystem, setIsSavingSystem] = useState(false);
  const [settingsLoadError, setSettingsLoadError] = useState<string | null>(null);
  const theme = useTheme();
  const { user, updateUser } = useAuth();
  const { toggleTheme, setPrimaryColor, mode } = useAppTheme();

  // State for notification settings
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    caseUpdates: true,
    documentUploads: true,
    billingReminders: true,
    calendarEvents: true,
    securityAlerts: true,
    marketingEmails: false,
  });

  // State for appearance settings
  const [appearanceSettings, setAppearanceSettings] = useState<AppearanceSettings>({
    theme: mode === 'dark' ? 'dark' : 'light',
    primaryColor: '#1976d2',
    fontSize: 'medium',
    compactMode: false,
    showAnimations: true,
    sidebarCollapsed: false,
  });

  // State for system settings
  const [systemSettings, setSystemSettings] = useState<SystemSettings>({
    autoSave: true,
    autoBackup: true,
    dataRetention: 90,
    maxFileSize: 10,
    allowedFileTypes: ['pdf', 'doc', 'docx', 'txt', 'jpg', 'png'],
    sessionTimeout: 30,
    language: 'en',
    timezone: 'America/New_York',
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = getAuthToken();
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [notifRes, appearRes, systemRes] = await Promise.all([
          fetch('/api/settings/notifications', { headers }),
          fetch('/api/settings/appearance', { headers }),
          fetch('/api/settings/system', { headers }),
        ]);
        if (cancelled) return;
        if (notifRes.ok) {
          const json = await notifRes.json();
          if (json?.data) setNotificationSettings((prev) => ({ ...prev, ...json.data }));
        }
        if (appearRes.ok) {
          const json = await appearRes.json();
          if (json?.data) {
            setAppearanceSettings((prev) => ({ ...prev, ...json.data }));
            if (json.data.primaryColor) setPrimaryColor(json.data.primaryColor);
          }
        }
        if (systemRes.ok) {
          const json = await systemRes.json();
          if (json?.data) {
            setSystemSettings((prev) => ({
              ...prev,
              ...json.data,
              sessionTimeout:
                json.data.sessionTimeout != null ? json.data.sessionTimeout : prev.sessionTimeout,
            }));
          }
        }
        setSettingsLoadError(null);
      } catch (err) {
        if (!cancelled) {
          setSettingsLoadError(toUserErrorMessage(err, 'Failed to load settings.'));
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Intentionally once on mount — avoid re-toggling theme on mode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenProfileDialog = React.useCallback(() => {
    if (user) {
      // Format phone number to ensure it meets backend requirements
      let formattedPhone = user.phone || '';
      if (formattedPhone) {
        // Remove any non-digit characters except +
        formattedPhone = formattedPhone.replace(/[^\d+]/g, '');
        // Ensure it starts with + if it's a valid international format
        if (formattedPhone.length >= 10 && !formattedPhone.startsWith('+')) {
          formattedPhone = '+' + formattedPhone;
        }
      }

      setProfileFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: formattedPhone,
        role: user.role,
        department: user.specialization || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zipCode: user.zipCode || '',
        timezone: user.timezone || 'America/New_York',
        language: 'en',
      });
    }
    setIsProfileDialogOpen(true);
  }, [user]);



  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    timezone: 'America/New_York',
    language: 'en',
  });

  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Notification settings handlers
  const handleNotificationChange = (setting: keyof NotificationSettings, value: boolean) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Appearance settings handlers — wire to ThemeContext for dark mode / brand color
  const handleAppearanceChange = (setting: keyof AppearanceSettings, value: any) => {
    setAppearanceSettings(prev => ({
      ...prev,
      [setting]: value
    }));
    if (setting === 'theme') {
      const next = value === 'dark' ? 'dark' : 'light';
      if (next !== mode) {
        toggleTheme();
      }
    }
    if (setting === 'primaryColor' && typeof value === 'string') {
      setPrimaryColor(value);
    }
  };

  // System settings handlers
  const handleSystemChange = (setting: keyof SystemSettings, value: any) => {
    setSystemSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSaveProfile = React.useCallback(async () => {
    if (!user) {
      alert('No user data available');
      return;
    }

    // Validate required fields
    if (!profileFormData.firstName.trim() || !profileFormData.lastName.trim() || !profileFormData.email.trim()) {
      alert('First name, last name, and email are required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileFormData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    // Validate phone number format (7-15 digits, may start with +)
    if (profileFormData.phone && profileFormData.phone.trim()) {
      const phoneRegex = /^\+?[0-9]{7,15}$/;
      if (!phoneRegex.test(profileFormData.phone.trim())) {
        alert('Phone number must be between 7 and 15 digits and may start with +');
        return;
      }
    }

    setIsSavingProfile(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      // Self-service profile — no users:write required
      const response = await fetch('/api/settings/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: profileFormData.firstName.trim(),
          lastName: profileFormData.lastName.trim(),
          phone: profileFormData.phone.trim() || null,
          specialization: profileFormData.department.trim() || null,
          address: profileFormData.address.trim() || null,
          city: profileFormData.city.trim() || null,
          state: profileFormData.state.trim() || null,
          zipCode: profileFormData.zipCode.trim() || null,
          timezone: profileFormData.timezone,
          language: profileFormData.language,
        }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update profile.'));
      }

      const result = await response.json();
      
      if (result.data) {
        updateUser({
          ...user,
          firstName: profileFormData.firstName.trim(),
          lastName: profileFormData.lastName.trim(),
          phone: profileFormData.phone.trim() || null,
          specialization: profileFormData.department.trim() || null,
          address: profileFormData.address.trim() || null,
          city: profileFormData.city.trim() || null,
          state: profileFormData.state.trim() || null,
          zipCode: profileFormData.zipCode.trim() || null,
          timezone: profileFormData.timezone,
        });
        alert('Profile updated successfully!');
      }
      
      setIsProfileDialogOpen(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert(toUserErrorMessage(error, 'Failed to update profile. Please try again.'));
    } finally {
      setIsSavingProfile(false);
    }
  }, [user, profileFormData, updateUser]);

  const authHeaders = () => {
    const token = getAuthToken();
    if (!token) throw new Error('No authentication token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const handleSaveNotifications = async () => {
    setIsSavingNotifications(true);
    try {
      const response = await fetch('/api/settings/notifications', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(notificationSettings),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to save notification settings.'));
      }
      const result = await response.json();
      if (result?.data) setNotificationSettings((prev) => ({ ...prev, ...result.data }));
      alert('Notification settings saved.');
    } catch (error) {
      alert(toUserErrorMessage(error, 'Failed to save notification settings.'));
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const handleSaveAppearance = async () => {
    setIsSavingAppearance(true);
    try {
      const response = await fetch('/api/settings/appearance', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(appearanceSettings),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to save appearance settings.'));
      }
      const result = await response.json();
      if (result?.data) setAppearanceSettings((prev) => ({ ...prev, ...result.data }));
      alert('Appearance settings saved.');
    } catch (error) {
      alert(toUserErrorMessage(error, 'Failed to save appearance settings.'));
    } finally {
      setIsSavingAppearance(false);
    }
  };

  const handleSaveSystem = async () => {
    setIsSavingSystem(true);
    try {
      const { sessionTimeout: _ignored, ...payload } = systemSettings;
      const response = await fetch('/api/settings/system', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to save system settings.'));
      }
      const result = await response.json();
      if (result?.data) {
        setSystemSettings((prev) => ({
          ...prev,
          ...result.data,
          sessionTimeout: prev.sessionTimeout,
        }));
        if (result.data.timezone && user) {
          updateUser({ ...user, timezone: result.data.timezone });
        }
      }
      alert('System settings saved.');
    } catch (error) {
      alert(toUserErrorMessage(error, 'Failed to save system settings.'));
    } finally {
      setIsSavingSystem(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }

    const strength = evaluatePassword(passwordFormData.newPassword);
    if (!strength.valid) {
      alert(`Password requirements not met: ${strength.errors.join(', ')}`);
      return;
    }

    setIsChangingPassword(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch('/api/settings/password', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: passwordFormData.currentPassword,
          newPassword: passwordFormData.newPassword,
        }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to change password.'));
      }

      const result = await response.json();
      console.log('Password changed successfully:', result);
      
      alert('Password changed successfully!');
      setIsPasswordDialogOpen(false);
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Failed to change password:', error);
      alert(toUserErrorMessage(error, 'Failed to change password. Please try again.'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const renderProfileSettings = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 80, height: 80 }}>
                  {user?.firstName?.charAt(0) || ''}{user?.lastName?.charAt(0) || ''}
                </Avatar>
                <Box>
                  <Typography variant="h5" component="div">
                    {user?.firstName || ''} {user?.lastName || ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div">
                    {user?.role || ''} • {user?.specialization || ''}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" component="div">
                    Email
                  </Typography>
                  <Typography variant="body1" component="div">
                    {user?.email || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" component="div">
                    Phone
                  </Typography>
                  <Typography variant="body1" component="div">
                    {user?.phone || 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" component="div">
                    Address
                  </Typography>
                  <Typography variant="body1" component="div">
                    {user?.address ? `${user.address}, ${user.city || ''}, ${user.state || ''} ${user.zipCode || ''}`.replace(/,\s*,/g, ',').replace(/^\s*,\s*/, '').replace(/,\s*$/, '') : 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" component="div">
                    Timezone
                  </Typography>
                  <Typography variant="body1" component="div">
                    {user?.timezone || 'America/New_York'}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={handleOpenProfileDialog}
                >
                  Edit Profile
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Security />}
                  onClick={() => setIsPasswordDialogOpen(true)}
                  sx={{ ml: 2 }}
                >
                  Change Password
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="div">
                Account Information
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Info />
                  </ListItemIcon>
                  <ListItemText
                    primary="Account Status"
                    secondary={
                      <Box component="span">
                        <Chip label="Active" color="success" size="small" />
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email Verified"
                    secondary="Verified on Jan 15, 2024"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security />
                  </ListItemIcon>
                  <ListItemText
                    primary="Two-Factor Auth"
                    secondary={
                      <Box component="span">
                        <Chip label="Enabled" color="success" size="small" />
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Storage />
                  </ListItemIcon>
                  <ListItemText
                    primary="Storage Used"
                    secondary="2.4 GB of 10 GB"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderNotificationSettings = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSaveNotifications}
              disabled={isSavingNotifications}
            >
              {isSavingNotifications ? 'Saving...' : 'Save Notifications'}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="div">
                Email Notifications
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Case Updates"
                    secondary="Get notified when case status changes"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.caseUpdates}
                      onChange={(e) => handleNotificationChange('caseUpdates', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Document Uploads"
                    secondary="Notifications for new document uploads"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.documentUploads}
                      onChange={(e) => handleNotificationChange('documentUploads', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Billing Reminders"
                    secondary="Payment due notifications"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.billingReminders}
                      onChange={(e) => handleNotificationChange('billingReminders', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Calendar Events"
                    secondary="Meeting and deadline reminders"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.calendarEvents}
                      onChange={(e) => handleNotificationChange('calendarEvents', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="div">
                Security & System
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Security Alerts"
                    secondary="Login attempts and security events"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.securityAlerts}
                      onChange={(e) => handleNotificationChange('securityAlerts', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Marketing Emails"
                    secondary="Newsletters and promotional content"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.marketingEmails}
                      onChange={(e) => handleNotificationChange('marketingEmails', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="SMS Notifications"
                    secondary="Text message notifications"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.smsNotifications}
                      onChange={(e) => handleNotificationChange('smsNotifications', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Push Notifications"
                    secondary="Browser and mobile push notifications"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={notificationSettings.pushNotifications}
                      onChange={(e) => handleNotificationChange('pushNotifications', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderAppearanceSettings = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSaveAppearance}
              disabled={isSavingAppearance}
            >
              {isSavingAppearance ? 'Saving...' : 'Save Appearance'}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="div">
                Theme & Colors
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Theme</InputLabel>
                <Select
                  value={appearanceSettings.theme}
                  label="Theme"
                  onChange={(e) => handleAppearanceChange('theme', e.target.value)}
                >
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                  <MenuItem value="auto">Auto (System)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Primary Color</InputLabel>
                <Select
                  value={appearanceSettings.primaryColor}
                  label="Primary Color"
                  onChange={(e) => handleAppearanceChange('primaryColor', e.target.value)}
                >
                  <MenuItem value="#1976d2">Blue</MenuItem>
                  <MenuItem value="#388e3c">Green</MenuItem>
                  <MenuItem value="#d32f2f">Red</MenuItem>
                  <MenuItem value="#f57c00">Orange</MenuItem>
                  <MenuItem value="#7b1fa2">Purple</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Font Size</InputLabel>
                <Select
                  value={appearanceSettings.fontSize}
                  label="Font Size"
                  onChange={(e) => handleAppearanceChange('fontSize', e.target.value)}
                >
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="large">Large</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={appearanceSettings.compactMode}
                    onChange={(e) => handleAppearanceChange('compactMode', e.target.checked)}
                  />
                }
                label="Compact Mode"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="div">
                Interface Options
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={appearanceSettings.showAnimations}
                    onChange={(e) => handleAppearanceChange('showAnimations', e.target.checked)}
                  />
                }
                label="Show Animations"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={appearanceSettings.sidebarCollapsed}
                    onChange={(e) => handleAppearanceChange('sidebarCollapsed', e.target.checked)}
                  />
                }
                label="Collapsed Sidebar"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderSystemSettings = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSaveSystem}
              disabled={isSavingSystem}
            >
              {isSavingSystem ? 'Saving...' : 'Save System Settings'}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="div">
                Data & Storage
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={systemSettings.autoSave}
                    onChange={(e) => handleSystemChange('autoSave', e.target.checked)}
                  />
                }
                label="Auto Save"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={systemSettings.autoBackup}
                    onChange={(e) => handleSystemChange('autoBackup', e.target.checked)}
                  />
                }
                label="Auto Backup"
              />
              <TextField
                label="Data Retention (days)"
                type="number"
                value={systemSettings.dataRetention}
                onChange={(e) => handleSystemChange('dataRetention', parseInt(e.target.value))}
                fullWidth
                sx={{ mt: 2 }}
              />
              <TextField
                label="Max File Size (MB)"
                type="number"
                value={systemSettings.maxFileSize}
                onChange={(e) => handleSystemChange('maxFileSize', parseInt(e.target.value))}
                fullWidth
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom component="div">
                Session & Language
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Session timeout is managed under Security → Security Settings (hours).
              </Alert>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Language</InputLabel>
                <Select
                  value={systemSettings.language}
                  label="Language"
                  onChange={(e) => handleSystemChange('language', e.target.value)}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={systemSettings.timezone}
                  label="Timezone"
                  onChange={(e) => handleSystemChange('timezone', e.target.value)}
                >
                  <MenuItem value="America/New_York">Eastern Time</MenuItem>
                  <MenuItem value="America/Chicago">Central Time</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };



  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Settings sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
      </Box>

      {settingsLoadError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSettingsLoadError(null)}>
          {settingsLoadError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
          <Tab label="Profile" icon={<Person />} iconPosition="start" />
          <Tab label="Notifications" icon={<Notifications />} iconPosition="start" />
          <Tab label="Appearance" icon={<Palette />} iconPosition="start" />
          <Tab label="System" icon={<Storage />} iconPosition="start" />
          <Tab label="Data Portability" icon={<ImportExport />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {renderProfileSettings()}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {renderNotificationSettings()}
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        {renderAppearanceSettings()}
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        {renderSystemSettings()}
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <DataPortabilityPanel />
      </TabPanel>

      {/* Dialogs */}
      <ProfileDialog 
        open={isProfileDialogOpen}
        onClose={() => setIsProfileDialogOpen(false)}
        profileFormData={profileFormData}
        setProfileFormData={setProfileFormData}
        isSavingProfile={isSavingProfile}
        onSave={handleSaveProfile}
      />
      <PasswordDialog
        open={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
        passwordFormData={passwordFormData}
        setPasswordFormData={setPasswordFormData}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        isChangingPassword={isChangingPassword}
        onSave={handleChangePassword}
      />
    </Box>
  );
};

export default SettingsPage; 